import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import { Auth } from "aws-amplify";
import { AUTH_ERROR_MESSAGES, PHONE_COUNTRY_CODES } from "../constants";
import {
  FormControl,
  Button,
  Spinner,
  FormGroup,
  Toast,
} from "react-bootstrap";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

const validationSchema = Yup.object().shape({
  email: Yup.string().email("*Invalid email").required("*Email Required"),
  password: Yup.string()
    .min(8, "*Password must have at least 8 characters")
    .max(50, "*Password can't be longer than 50 characters")
    .required("*Password is required"),
  phoneNumber: Yup.string()
    .required("*Phone number Required")
    .min(6, "*Phone number must have at least 6 characters")
    .max(15, "*Password can't be longer than 15 characters"),
});
const validAuthStates = ["signUp"];

const CustomSignUp = ({ authState, onStateChange }) => {
  const [showToast, setShowToast] = useState(false);

  const [signUpStatus, setSignUpStatus] = useState({
    isProcessing: false,
    errorMsg: "",
  });
  const [phoneCode, setPhoneCode] = useState(PHONE_COUNTRY_CODES[0]);

  const toggleToast = () => {
    setShowToast(!showToast);
  };

  useEffect(() => {
    setSignUpStatus({ isProcessing: false, errorMsg: "" });
  }, [authState]);

  const signUpSubmit = async (values) => {
    const { email, password, phoneNumber } = values;
    const composePhoneNumber = `${phoneCode || "+1"}${phoneNumber.replace(
      /[-()]/g,
      ""
    )}`;

    try {
      setSignUpStatus({
        isProcessing: true,
        errorMsg: "",
      });
      const { userConfirmed } = await Auth.signUp({
        username: email,
        password: password,
        attributes: {
          email: email,
          phone_number: composePhoneNumber,
        },
      });

      setSignUpStatus({
        errorMsg: "",
        isProcessing: false,
      });
      toggleToast();

      if (!userConfirmed) {
        onStateChange("accountConfirm", { email });
      }
    } catch (error) {
      const { code, message } = error;
      setSignUpStatus({
        errorMsg:
          (code ? AUTH_ERROR_MESSAGES[code] : message) ||
          AUTH_ERROR_MESSAGES.default,
        isProcessing: false,
      });
    }
  };

  const onChangePhoneCode = (e) => {
    setPhoneCode(e.target.value);
  };

  return (
    <>
      <Toast show={showToast} onClose={toggleToast} autohide>
        <Toast.Body>
          Create account success.
          <br />
          Please verify your account!
        </Toast.Body>
      </Toast>
      {validAuthStates.includes(authState) ? (
        <Formik
          initialValues={{
            email: "",
            password: "",
            phoneNumber: "",
          }}
          validationSchema={validationSchema}
          onSubmit={signUpSubmit}
        >
          {({ errors, touched }) => (
            <Form className="auth-form">
              <Field name="email">
                {({ field }) => (
                  <FormGroup controlId="email">
                    <FormControl
                      type="email"
                      placeholder="Email"
                      value={field.value}
                      onChange={field.onChange}
                      data-prop="email"
                      className="auth-input"
                      isValid={touched.email && !errors.email}
                      isInvalid={touched.email && errors.email}
                      data-testid="email-signup"
                    />
                    <FormControl.Feedback type="invalid">
                      {errors.email}
                    </FormControl.Feedback>
                  </FormGroup>
                )}
              </Field>
              <Field name="password">
                {({ field }) => (
                  <FormGroup controlId="password">
                    <FormControl
                      type="password"
                      placeholder="Password"
                      value={field.value}
                      onChange={field.onChange}
                      data-prop="password"
                      className="auth-input"
                      isValid={touched.password && !errors.password}
                      isInvalid={touched.password && errors.password}
                      data-testid="password-signup"
                    />
                    <FormControl.Feedback type="invalid">
                      {errors.password}
                    </FormControl.Feedback>
                  </FormGroup>
                )}
              </Field>
              <Field name="phoneNumber">
                {({ field }) => (
                  <div className="auth-phone-number">
                    <select
                      name="phone_code"
                      className="select-phone-code"
                      defaultValue={PHONE_COUNTRY_CODES[0]}
                      onChange={onChangePhoneCode}
                      data-testid="phone-code-signup"
                    >
                      {PHONE_COUNTRY_CODES.map((phoneCode) => (
                        <option key={phoneCode} value={phoneCode}>
                          {phoneCode}
                        </option>
                      ))}
                    </select>
                    <FormGroup controlId="phoneNumber">
                      <FormControl
                        type="tel"
                        placeholder="Phone number"
                        onChange={field.onChange}
                        data-testid="phone-number-signup"
                        value={field.value}
                        data-prop="phoneNumber"
                        className="auth-input"
                        isValid={touched.phoneNumber && !errors.phoneNumber}
                        isInvalid={touched.phoneNumber && errors.phoneNumber}
                      />
                      <FormControl.Feedback type="invalid">
                        {errors.phoneNumber}
                      </FormControl.Feedback>
                    </FormGroup>
                  </div>
                )}
              </Field>

              {signUpStatus.errorMsg && (
                <p className="error-message">{signUpStatus.errorMsg}</p>
              )}
              <Button type="submit" disabled={signUpStatus.isProcessing}>
                Sign up
                {signUpStatus.isProcessing && (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="isProcessing"
                    className="ml-1"
                    aria-hidden="true"
                  />
                )}
              </Button>
              <p>
                {`Have an account?`}{" "}
                <Button
                  variant="link"
                  onClick={() => onStateChange("signIn")}
                  data-testid="signin"
                >
                  <strong>Sign in</strong>
                </Button>
              </p>
            </Form>
          )}
        </Formik>
      ) : null}
    </>
  );
};

CustomSignUp.propTypes = {
  authState: PropTypes.string,
  onStateChange: PropTypes.func,
};

export default CustomSignUp;
